import { z } from "zod";
import { idempotencyKey } from "./requests.js";
import type { DocClass } from "./vocabulary.js";

export const photoPurposes = ["damage", "repair_before", "repair_after", "other"] as const;
export type PhotoPurpose = (typeof photoPurposes)[number];
export const photoPurposeLabels: Record<PhotoPurpose, string> = {
  damage: "Damage", repair_before: "Before repair", repair_after: "After repair", other: "Other photo",
};
export const editableDocumentClasses = ["photo", "dot_inspection", "work_order", "other", "unknown"] as const;
export const assetFileCategories = ["damage", "dot_inspection", "receipt", "invoice", "estimate", "repair_before", "repair_after", "photo", "other", "unknown"] as const;
export type AssetFileCategory = (typeof assetFileCategories)[number];
export const assetFileCategoryLabels: Record<AssetFileCategory, string> = {
  damage: "Damage", dot_inspection: "DOT results", receipt: "Receipts", invoice: "Invoices",
  estimate: "Estimates", repair_before: "Before repair", repair_after: "After repair",
  photo: "Photos", other: "Other documents", unknown: "Unclassified",
};

/** Money and DOT classes take precedence over a photo's optional purpose. */
export function assetFileCategory(docClass: DocClass, photoPurpose: PhotoPurpose | null, mimeType: string, financialRelationship: string | null = null): AssetFileCategory {
  // Preserve a known financial type: logging a receipt creates accounting
  // evidence but must not move that receipt into the Invoices category.
  if (docClass === "invoice" || docClass === "receipt" || docClass === "estimate") return docClass;
  if (financialRelationship === "invoice" || financialRelationship === "receipt" || financialRelationship === "estimate") return financialRelationship;
  if (docClass === "dot_inspection") return "dot_inspection";
  if (mimeType.startsWith("image/") && photoPurpose && photoPurpose !== "other") return photoPurpose;
  if (mimeType.startsWith("image/") && (docClass === "photo" || docClass === "unknown")) {
    return "photo";
  }
  if (docClass === "unknown") return "unknown";
  return "other";
}

export const assetFilesQuery = z.object({
  category: z.enum(["all", ...assetFileCategories]).default("all"),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(48).default(24),
  workOrderId: z.string().uuid().optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
}).strict().refine(value => !value.from || !value.to || value.from <= value.to, { message: "End date cannot be before start date.", path: ["to"] });
export type AssetFilesQuery = z.infer<typeof assetFilesQuery>;

export const relabelFilesBody = z.object({
  documentIds: z.array(z.string().uuid()).min(1).max(50).refine(ids => new Set(ids).size === ids.length, "Select each file once."),
  docClass: z.enum(editableDocumentClasses).optional(),
  photoPurpose: z.enum(photoPurposes).nullable().optional(),
  idempotencyKey,
}).strict().refine(value => value.docClass !== undefined || value.photoPurpose !== undefined, "Choose a document type or photo purpose.");
export type RelabelFilesBody = z.infer<typeof relabelFilesBody>;

export interface AssetFileSource {
  kind: "asset_photo" | "asset_document" | "work_order";
  assetId: string;
  workOrderId: string | null;
  workOrderDisplayId: string | null;
  workOrderTitle: string | null;
}
export interface AssetFile {
  documentId: string;
  fileName: string | null;
  docClass: DocClass;
  photoPurpose: PhotoPurpose | null;
  category: AssetFileCategory;
  mimeType: string;
  byteSize: number;
  attachedAt: string;
  canPreview: boolean;
  thumbnailUrl: string | null;
  financial: boolean;
  canRelabel: boolean;
  relabelReason: string | null;
  sources: AssetFileSource[];
}
export interface AssetFilesResponse {
  rows: AssetFile[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<AssetFileCategory, number>;
  withheldFinancialCount: number;
}
export interface RelabelFilesResponse { documentIds: string[] }
